import { useState } from "react";
import DSBaseTable, {
  Column,
} from "views/system-design/Table/DSBaseTable/components/DSBaseTable";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSCollapsibleText from "views/system-design/DSCollapsibleText";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSIconButton from "views/system-design/DSIconButton";
import DSTypography from "views/system-design/DSTypography";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSAlert from "views/system-design/DSAlert";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  useGroupedClockErrors,
  useDeleteClockError,
} from "../hooks/useGroupedClockErrors";
import { useCreateIgnoredClockError } from "../hooks/useIgnoredClockErrors";
import { GroupedClockError } from "api/admin";
import firebaseService from "firebase_service/firebaseService";

const columns: Column<GroupedClockError>[] = [
  { field: "log", label: "Log" },
  { field: "method", label: "Method" },
  { field: "class", label: "Class" },
  { field: "occurrences", label: "Occurrences" },
  { field: "lastOccurrence", label: "Last Occurrence" },
  { field: "clocks", label: "Clocks" },
  { field: "isDeleted", label: "Deleted" },
];

const formatClocksList = (clocks: GroupedClockError["clocks"]) =>
  clocks.map((clock) => `${clock.clockId} (${clock.count})`).join(", ");

const ErrorsTable = () => {
  const { data, isLoading } = useGroupedClockErrors();
  const {
    mutate: deleteClockError,
    variables: pendingDeleteParams,
    isPending: isDeleting,
    error: deleteError,
    reset: resetDeleteError,
  } = useDeleteClockError();
  const {
    mutate: createIgnoredError,
    isPending: isIgnoring,
    error: ignoreError,
    reset: resetIgnoreError,
  } = useCreateIgnoredClockError();
  const [confirmDeleteRow, setConfirmDeleteRow] =
    useState<GroupedClockError | null>(null);

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  const getRowId = (row: GroupedClockError) =>
    `${row.errorMessage}-${row.class}-${row.method}`;

  const isDeletingRow = (row: GroupedClockError) =>
    isDeleting &&
    pendingDeleteParams?.errorMessage === row.errorMessage &&
    pendingDeleteParams?.method === row.method &&
    pendingDeleteParams?.class === row.class;

  const handleIgnore = (row: GroupedClockError) => {
    const publicVersion = firebaseService.general?.public;
    if (!publicVersion) return;
    createIgnoredError({
      errorMessage: row.errorMessage,
      method: row.method,
      class: row.class,
      version: publicVersion,
    });
  };

  const handleConfirmDelete = () => {
    if (confirmDeleteRow) {
      deleteClockError({
        errorMessage: confirmDeleteRow.errorMessage,
        method: confirmDeleteRow.method,
        class: confirmDeleteRow.class,
      });
    }
    setConfirmDeleteRow(null);
  };

  const rowBuilder = (row: GroupedClockError) => (
    <>
      <DSTableCell sx={{ minWidth: 400, maxWidth: 700 }}>
        <DSCollapsibleText text={row.log} maxLines={2} />
      </DSTableCell>
      <DSTableCell>{row.method}</DSTableCell>
      <DSTableCell>{row.class}</DSTableCell>
      <DSTableCell>{row.occurrences}</DSTableCell>
      <DSTableCell>
        {row.lastOccurrence
          ? new Date(row.lastOccurrence).toLocaleString()
          : ""}
      </DSTableCell>
      <DSTableCell sx={{ minWidth: 300 }}>
        <DSCollapsibleText text={formatClocksList(row.clocks)} maxLines={2} />
      </DSTableCell>
      <DSTableCell>
        {isDeletingRow(row) ? (
          <DSCircularProgress size={16} />
        ) : row.isDeleted ? (
          "true"
        ) : (
          <DSBox sx={{ display: "flex", gap: 0.5 }}>
            <DSIconButton
              size="small"
              onClick={() => setConfirmDeleteRow(row)}
              title="Delete error logs"
            >
              <DeleteIcon color="error" fontSize="small" />
            </DSIconButton>
            <DSIconButton
              size="small"
              onClick={() => handleIgnore(row)}
              disabled={isIgnoring}
              title="Ignore in monitor"
            >
              <VisibilityOffIcon color="warning" fontSize="small" />
            </DSIconButton>
          </DSBox>
        )}
      </DSTableCell>
    </>
  );

  return (
    <>
      {deleteError && (
        <DSAlert severity="error" onClose={resetDeleteError} sx={{ mb: 2 }}>
          Failed to delete error: {deleteError.message}
        </DSAlert>
      )}
      {ignoreError && (
        <DSAlert severity="error" onClose={resetIgnoreError} sx={{ mb: 2 }}>
          Failed to ignore error: {(ignoreError as Error).message}
        </DSAlert>
      )}
      <DSBaseTable
        data={data ?? []}
        columns={columns}
        rowBuilder={rowBuilder}
        getRowId={getRowId}
        defaultRowsPerPage={25}
      />
      <DSDialog
        open={!!confirmDeleteRow}
        onClose={() => setConfirmDeleteRow(null)}
      >
        <DSDialogTitle>Delete Error</DSDialogTitle>
        <DSDialogContent>
          <DSTypography>
            Mark all logs matching this error as deleted?
          </DSTypography>
          {confirmDeleteRow && (
            <DSTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {confirmDeleteRow.log}
            </DSTypography>
          )}
        </DSDialogContent>
        <DSDialogActions>
          <DSButton onClick={() => setConfirmDeleteRow(null)}>Cancel</DSButton>
          <DSButton
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            Delete
          </DSButton>
        </DSDialogActions>
      </DSDialog>
    </>
  );
};

export default ErrorsTable;
